import { destinationHistory } from './destinationHistory';
import { getUser } from './auth.js';
import type { ClientDestination } from './uiCommands';
import { resolveSettingsPage } from './settingsNavigation';
import type { DocumentListState } from './documentDestination';
function key(name: string) { const user = getUser(); return user?.id && user.organization?.id ? `cohesion:${user.organization.id}:${user.id}:${name}` : null; }
/** Store navigation and query state only, never evidence or financial results.
 * Namespacing prevents another signed-in user inheriting a project selection. */
export function readClientState<T>(name: string, fallback: T): T {
  try { const storageKey = key(name); return storageKey ? JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') ?? fallback : fallback; } catch { return fallback; }
}
export function saveClientState(name: string, value: unknown) { try { const storageKey = key(name); if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* Private browsing may disable storage; API state remains authoritative. */ } }
export function saveDestination(destination: ClientDestination) { saveClientState('destination', destination); }
export function readDestination(shell?: 'desktop' | 'mobile'): ClientDestination | null {
  const value = readClientState<ClientDestination | null>('destination', null);
  if (!value) return value;
  if (value.settingsPage !== undefined) {
    const page = resolveSettingsPage(value.settingsPage);
    if (!page) return null;
    value.settingsPage = page;
  }
  if (!shell) return value;
  const desktop: Record<string, string> = { home: 'dashboard', projects: 'projekte', times: 'plans', calendar: 'termine', contacts: 'customers2', offers: 'documents', invoices: 'documents', vouchers: 'gutscheine', settings: 'einstellungen' };
  const mobile: Record<string, string> = { dashboard: 'home', projekte: 'projects', plans: 'times', termine: 'calendar', customers2: 'contacts', documents: 'invoices', gutscheine: 'vouchers', einstellungen: 'settings' };
  const mapping = shell === 'desktop' ? desktop : mobile;
  return { ...value, section: mapping[value.section] || value.section, returnTo: value.returnTo ? { ...value.returnTo, section: mapping[value.returnTo.section] || value.returnTo.section } : undefined };
}
const guards = new Set<() => boolean>();
export function registerNavigationGuard(guard: () => boolean) { guards.add(guard); return () => { guards.delete(guard); }; }
export function canLeaveDestination() { return [...guards].every((guard) => guard()); }
/** Native destinations participate in browser back/forward without rewriting
 * legacy routes. Only small navigation IDs live in history. */
export function pushDestination(destination: ClientDestination) {
  if (destination.settingsPage !== undefined) {
    const page = resolveSettingsPage(destination.settingsPage);
    if (!page || !['settings', 'einstellungen'].includes(destination.section)) return false;
    destination = { ...destination, settingsPage: page };
  }
  const previous = activeHistory?.current() ?? readDestination();
  const next = { ...destination, returnTo: destination.returnTo ?? (previous ? { ...previous, returnTo: undefined } : undefined) };
  try { if (activeHistory && !activeHistory.push(next)) return false; } catch { /* Embedded WebViews may restrict history. */ }
  saveDestination(next);
  return true;
}

/** A capture shortcut is a one-shot launch, never a draft stored in history. */
export function consumeCostDestinationAction() {
  const destination = activeHistory?.current() ?? readDestination();
  if (destination?.section !== 'costs' || !destination.costAction) return;
  const next = { ...destination }; delete next.costAction;
  activeHistory?.replace(next); saveDestination(next);
}
export function consumeSupplierExpenseDestination(recordId?: string) {
  const destination = activeHistory?.current() ?? readDestination();
  if (destination?.section !== 'costs' || !destination.supplierJobId) return;
  const next = { ...destination }; delete next.supplierJobId;
  if (recordId) next.recordId = recordId;
  activeHistory?.replace(next); saveDestination(next);
}
export function consumeTimeDestinationAction() {
  const destination = activeHistory?.current() ?? readDestination();
  if (!['times', 'plans'].includes(destination?.section ?? '') || !destination?.timeAction) return;
  const next = { ...destination }; delete next.timeAction;
  activeHistory?.replace(next); saveDestination(next);
}
/** An uploaded tender replaces its one-shot upload destination without remounting
 * the editor. Only saved identity is stored; never file bytes or analysis. */
export function replaceTenderDestinationRecord(recordId: string, projectId: string) {
  const current = activeHistory?.current() ?? readDestination();
  if (current?.section !== 'ausschreibung') return;
  const next = { ...current, recordId, projectId, projectScope: 'project' as const };
  delete next.tenderAction;
  activeHistory?.replace(next); saveDestination(next);
}
export function returnToDestination() { return activeHistory?.returnTo() ?? false; }
export function currentDestination() { return activeHistory?.current() ?? readDestination(); }
/** Replacements keep capture mounted; a distinct Back/Forward entry remounts it. */
export function destinationEntryKey() { return activeHistory?.entryKey() ?? 'initial'; }
export function replaceCostDestinationScope(scope: { projectScope: 'all' | 'company' | 'project'; projectId?: string }) {
  const current = activeHistory?.current() ?? readDestination();
  if (current?.section !== 'costs') return;
  const next = { ...current, projectScope: scope.projectScope };
  if (scope.projectScope === 'project') next.projectId = scope.projectId;
  else delete next.projectId;
  activeHistory?.replace(next); saveDestination(next);
}

export function replaceDocumentDestinationScope(scope: { projectScope?: 'all' | 'company' | 'project'; projectId?: string }) {
  const current = activeHistory?.current() ?? readDestination();
  if (!current || !['documents', 'offers', 'invoices'].includes(current.section)) return;
  const next = { ...current, projectScope: scope.projectScope };
  if (scope.projectScope === 'project' || (!scope.projectScope && scope.projectId)) next.projectId = scope.projectId;
  else delete next.projectId;
  activeHistory?.replace(next); saveDestination(next);
}

/** Persist both list dimensions in this history entry, without replayable actions. */
export function replaceDocumentDestinationFilters(value: DocumentListState) {
  const current = activeHistory?.current() ?? readDestination();
  if (!current || !['documents', 'offers', 'invoices'].includes(current.section)) return;
  const next = { ...current, documentKind: value.kind, documentFilter: value.filter, trash: value.trashMode };
  activeHistory?.replace(next); saveDestination(next);
}

export function replaceTimeDestinationScope(project: string) {
  const current = activeHistory?.current() ?? readDestination();
  if (!current || !['times', 'plans'].includes(current.section)) return;
  const projectScope = project === 'all' ? 'all' : project === 'company' ? 'company' : 'project';
  const next: ClientDestination = { ...current, projectScope };
  if (projectScope === 'project') next.projectId = project;
  else delete next.projectId;
  activeHistory?.replace(next); saveDestination(next);
}
export function replaceProjectDestinationTab(projectId: string, tab: string) {
  const current = activeHistory?.current() ?? readDestination();
  if (!current || !['projects', 'projekte'].includes(current.section) || current.projectId !== projectId) return;
  const next = { ...current, tab }; activeHistory?.replace(next); saveDestination(next);
}

/** One active shell owns history. Switching shells replaces the subscription. */
let activeHistory: ReturnType<typeof destinationHistory> | undefined;
let detachHistory: (() => void) | undefined;
export function bindDestinationHistory(shell: string, initial: ClientDestination, apply: (value: ClientDestination) => void, allowed: (value: ClientDestination) => boolean) {
  detachHistory?.();
  const owner = `${key('history')}:${shell}`;
  activeHistory = destinationHistory(owner, initial, {
    read: () => history.state?.cohesionEntry,
    replace: (entry) => history.replaceState({ ...history.state, cohesionEntry: entry }, ''),
    push: (entry) => history.pushState({ ...history.state, cohesionEntry: entry }, ''),
    go: (delta) => history.go(delta),
  }, (value) => { saveDestination(value); apply(value); restoreDestinationScroll(value); }, allowed, canLeaveDestination);
  const controller = activeHistory;
  // A shell switch maps stored section names. Publish that mapped origin before
  // project shortcuts read it; otherwise mobile can inherit desktop 'projekte'.
  saveDestination(controller.current());
  const pop = (event: PopStateEvent) => controller.pop(event.state?.cohesionEntry);
  const scroll = (event: Event) => {
    if (restoringScroll) return;
    const element = event.target === document ? document.scrollingElement : event.target;
    if (!(element instanceof Element)) return;
    const selector = scrollSelector(element);
    if (!selector) return;
    const destination = { ...controller.current(), scroll: { ...controller.current().scroll, [selector]: element.scrollTop } };
    controller.replace(destination); saveDestination(destination);
  };
  window.addEventListener('popstate', pop);
  document.addEventListener('scroll', scroll, true);
  const detach = () => { window.removeEventListener('popstate', pop); document.removeEventListener('scroll', scroll, true); if (activeHistory === controller) activeHistory = undefined; };
  detachHistory = detach;
  return detach;
}
const scrollRoots = ['html', '.cd-main', '.tw-app', '.lm-screen', '.cd-docs-list', '.pl-body'];
function scrollSelector(element: Element) { return scrollRoots.find((selector) => element.matches(selector)); }
let restoringScroll = false;
let cancelScroll: (() => void) | undefined;
export function restoreDestinationScroll(destination: ClientDestination) {
  cancelScroll?.();
  if (!destination.scroll) return;
  restoringScroll = true;
  const restore = () => Object.entries(destination.scroll || {}).forEach(([selector, top]) => {
    if (scrollRoots.includes(selector)) { const element = document.querySelector(selector); if (element) element.scrollTop = top; }
  });
  const observer = new MutationObserver(restore);
  observer.observe(document.body, { childList: true, subtree: true });
  const finish = () => { observer.disconnect(); clearTimeout(timeout); restoringScroll = false; document.removeEventListener('pointerdown', finish, true); document.removeEventListener('wheel', finish, true); document.removeEventListener('keydown', finish, true); };
  const timeout = window.setTimeout(finish, 2500);
  document.addEventListener('pointerdown', finish, true); document.addEventListener('wheel', finish, true); document.addEventListener('keydown', finish, true);
  cancelScroll = finish; requestAnimationFrame(restore);
}
